/**
 * Shared predicates for the Step 4 (Supporting evidence) scaffolding.
 */
import React from 'react';
import { titleUI } from 'platform/forms-system/src/js/web-component-patterns';
import { getNextPagePath } from 'platform/forms-system/src/js/routing';
import { getActiveExpandedPages } from 'platform/forms-system/src/js/helpers';
import {
  createArrayBuilderItemAddPath,
  onNavBackRemoveAddingItem,
  withUrlPrefix,
} from 'platform/forms-system/src/js/patterns/array-builder/helpers';

/**
 * Destination for entering an arrayBuilder loop when bypassing its intro
 * page:
 *  - the first item page in add mode when the array is empty
 *  - else, the loop's summary page
 *
 * Assumes the first item page is always active (no `depends`) — unlike
 * platform's `getFirstItemPagePath`, this always targets `itemPath` as-is and
 * won't skip it if a `depends` is added later.
 *
 * @param {{ arrayPath: string, itemPath: string, summaryPath: string }} options
 * @param {object} formData
 * @returns {string} path (no url prefix applied)
 */
export const getLoopEntryPath = (
  { arrayPath, itemPath, summaryPath },
  formData,
) => {
  const items = formData?.[arrayPath];
  return items?.length
    ? summaryPath
    : createArrayBuilderItemAddPath({ path: itemPath, index: 0 });
};

/**
 * used as `onNavForward` for a decision page that gates an arrayBuilder loop whose
 * intro page should never display. When `when(formData)` is true, jumps
 * straight into the loop (first item page in add mode, or the loop summary
 * if items already exist); otherwise continues with default navigation.
 *
 * @param {{
 *   when: (formData: any) => boolean,
 *   arrayPath: string,
 *   itemPath: string,
 *   summaryPath: string,
 * }} options
 */
export const navForwardSkippingLoopIntro =
  ({ when, ...loop }) =>
  ({ formData, goPath, goNextPath, urlPrefix }) => {
    if (when(formData)) {
      goPath(withUrlPrefix(getLoopEntryPath(loop, formData), urlPrefix));
    } else {
      goNextPath();
    }
  };

/**
 * used as `onNavForward` for a page that always enters an arrayBuilder loop
 * (first item page in add mode, or the loop summary if items already exist).
 * Unlike `navForwardSkippingLoopIntro`, there's no `when` gate — this is for
 * a page (e.g. an authorize-release/access page) whose Continue action
 * always proceeds into the loop, never past it.
 *
 * @param {{ arrayPath: string, itemPath: string, summaryPath: string }} loop
 */
export const onNavForwardIntoLoop =
  loop =>
  ({ formData, goPath, urlPrefix }) =>
    goPath(withUrlPrefix(getLoopEntryPath(loop, formData), urlPrefix));

/**
 * used as `onNavBack` for the first item page of an arrayBuilder loop whose
 * intro page is bypassed (see `listLoopPatternWithBypassedIntro`) but which
 * has multiple item sub-pages (so the generic `itemPage` piece of that
 * pattern — meant for single-page items — doesn't apply). Mirrors the
 * forward entry logic: Back from item 0 in add mode removes the
 * in-progress item and returns to `decisionPath` (rather than an empty
 * summary page), matching `onNavForwardIntoLoop`/`navForwardSkippingLoopIntro`.
 *
 * Only spread onto the loop's first item page — Back from later item pages
 * within the same item should use the default (previous sub-page) behavior.
 *
 * @param {{ arrayPath: string, summaryPath: string }} loop
 * @param {string} decisionPath path of the decision/access page (no url prefix)
 */
export const onNavBackFirstItemPage = (
  { arrayPath, summaryPath },
  decisionPath,
) =>
  onNavBackRemoveAddingItem({
    arrayPath,
    getSummaryPath: () => summaryPath,
    getIntroPath: () => decisionPath,
    reviewRoute: 'review-and-submit',
  });

/**
 * used as `CustomPage` for an arrayBuilder intro page that should never display.
 * Only needed when the loop's `required` is a function (e.g. service
 * treatment records) rather than literal `false`: platform only routes to
 * the intro page — via `ArrayBuilderCancelButton`/`useItemPageGuard` — when
 * `required(formData)` is true, so this is a real fallback in that case. For
 * `required: false` loops it would never be reached, so don't spread this
 * onto those loops' intro page config.
 */
export const skippedLoopIntroPage = loop => {
  return props => {
    React.useEffect(() => {
      props.goToPath(`/${getLoopEntryPath(loop, props.data)}`, { force: true });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return null;
  };
};

/**
 * Pattern: arrayBuilder list & loop with a bypassed intro page.
 *
 * Some Supporting Evidence loops skip their intro page, entering/exiting via
 * an app-level decision page instead. For a loop whose `required` is a
 * function (e.g. service treatment records), platform can still route to
 * the intro page when `required(formData)` is true — the `introPage` piece
 * below catches that case and redirects invisibly instead of showing a real
 * page. A loop with literal `required: false` never gets routed to its intro
 * page, so it should skip registering one (and skip the `introPage` piece
 * below) entirely — see privateMedicalRecordsUploadPages.js and
 * additionalEvidenceUploadPages.js.
 *
 * - decision page "yes": first item page (add mode) or loop summary (if items already exist)
 * - intro page (if registered): invisible redirect into the loop
 * - Back from the summary page --> goes to decision page
 * - Back from a list & loop item page --> goes to loop summary if items remain, else decision page
 *
 * @param {{ arrayPath: string, itemPath: string, summaryPath: string }} loop
 * @param {string} decisionPath path of the decision page (no url prefix)
 */
export const listLoopPatternWithBypassedIntro = (loop, decisionPath) => ({
  /** Spread onto the decision page; `when` gates entry into the loop. */
  decisionPage: when => ({
    onNavForward: navForwardSkippingLoopIntro({ when, ...loop }),
  }),
  /**
   * Spread onto pageBuilder.introPage's config — only for a loop whose
   * `required` is a function. Omit this (and the intro page entirely) for a
   * loop with literal `required: false`.
   */
  introPage: {
    CustomPage: skippedLoopIntroPage(loop),
    CustomPageReview: null,
  },
  /** Spread onto pageBuilder.summaryPage's config. */
  summaryPage: {
    onNavBack: ({ goPath, urlPrefix }) =>
      goPath(withUrlPrefix(decisionPath, urlPrefix)),
  },
  /** Spread onto pageBuilder.itemPage's config. */
  itemPage: {
    onNavBack: onNavBackRemoveAddingItem({
      arrayPath: loop.arrayPath,
      getSummaryPath: () => loop.summaryPath,
      getIntroPath: () => decisionPath,
      reviewRoute: 'review-and-submit',
    }),
  },
});

/**
 * placeholder schema until the implementation tickets fill in real fields.
 */
export const placeholderSchema = { type: 'object', properties: {} };

/**
 * placeholder uiSchema until the implementation tickets fill in real fields.
 *
 * @param {string} title - same string as the page's `title` key
 */
export const placeholderUiSchema = title => ({
  ...titleUI(title, 'placeholder content'),
});

/**
 * "Yes" on /supporting-evidence/medical-records — the Veteran has medical
 * records supporting their claim (same field as the all-claims v1 page).
 */
export const wantsEvidenceRequest = formData =>
  formData?.['view:hasMedicalRecords'] === true;

/**
 * "VA or military medical records" checked on
 * /supporting-evidence/medical-records-types.
 * TODO(#151946): confirm final field name/shape when building the page.
 */
export const hasVaMedicalRecords = formData =>
  wantsEvidenceRequest(formData) &&
  formData?.['view:medicalRecordTypes']?.vaOrMilitary === true;

/**
 * "Medical records from other healthcare providers" checked on
 * /supporting-evidence/medical-records-types.
 * TODO(#151945): confirm final field name/shape when building the page.
 */
export const hasPrivateMedicalRecords = formData =>
  wantsEvidenceRequest(formData) &&
  formData?.['view:medicalRecordTypes']?.privateProvider === true;

/**
 * "I want VA to request my medical records from other healthcare providers"
 * selected on /supporting-evidence/other-providers-sharing-options.
 * TODO(#151945): confirm final field name/values when building the page.
 */
export const wantsVaToRequestPrivateRecords = formData =>
  hasPrivateMedicalRecords(formData) &&
  formData?.['view:privateMedicalRecordsAccess'] === 'vaRequest';

/**
 * "I'll upload my records myself" selected on
 * /supporting-evidence/other-providers-sharing-options.
 * TODO(#155514): confirm final field name/values when building the page.
 */
export const willUploadPrivateRecords = formData =>
  hasPrivateMedicalRecords(formData) &&
  formData?.['view:privateMedicalRecordsAccess'] === 'selfUpload';

/**
 * "Yes" on /supporting-evidence/additional-evidence-intro — the Veteran has
 * supplemental documents or additional forms to upload. Same field shape as
 * the v1 evidenceChoiceIntro page (shared/pages/evidenceChoiceIntroPage).
 */
export const hasAdditionalEvidence = formData =>
  formData?.['view:selectableEvidenceTypes']?.['view:hasOtherEvidence'] ===
  true;

/**
 * The next page after a loop summary, skipping that loop's own item pages,
 * worked out from reconciled data.
 * Position comes from the page list's registration order, while which pages
 * are available comes from the reconciled data.
 * Expanded array pages keep their `pageKey`, which links the two.
 *
 * @param {Array} pageList
 * @param {object} projected reconciled form data, used to decide which pages
 *   are available
 * @param {string} pathname current (summary) path, url prefix included
 * @param {string} arrayPath the loop's arrayPath
 * @returns {string} path of the next page outside the loop. Falls back to
 *   platform's own `getNextPagePath`.
 */
export const getPathAfterLoop = (pageList, projected, pathname, arrayPath) => {
  // Registration order, so the summary is still findable after reconciling
  // has removed it from the available pages.
  const currentIndex = pageList.findIndex(page => page.path === pathname);
  if (currentIndex === -1) {
    return getNextPagePath(pageList, projected, pathname);
  }

  const orderByPageKey = new Map(
    pageList.map((page, index) => [page.pageKey, index]),
  );

  const next = getActiveExpandedPages(pageList, projected).find(page => {
    const order = orderByPageKey.get(page.pageKey);
    if (order === undefined || order <= currentIndex) {
      return false;
    }
    // Never return an item page of the loop we're leaving.
    return !(page.showPagePerItem && page.arrayPath === arrayPath);
  });

  return next?.path ?? getNextPagePath(pageList, projected, pathname);
};

/**
 * `onNavForward` for a loop summary page, replacing platform's default so
 * the next page can be worked out with `getPathAfterLoop` (which correctly
 * skips the loop's own expanded item pages) rather than platform's
 * `getNextPagePath`.
 *
 * @param {{
 *   arrayPath: string,
 *   itemPath: string,
 *   hasItemsKey: string,
 * }} loop
 */
export const navForwardFromSummary =
  ({ arrayPath, itemPath, hasItemsKey }) =>
  ({ formData, goPath, pageList, pathname, urlParams, urlPrefix }) => {
    // "Yes, add another" — same as platform's navForwardSummaryAddItem.
    if (formData?.[hasItemsKey]) {
      const nextIndex = formData?.[arrayPath]?.length || 0;
      goPath(
        withUrlPrefix(
          createArrayBuilderItemAddPath({
            path: itemPath,
            index: nextIndex,
            isReview: urlParams?.review,
          }),
          urlPrefix,
        ),
      );
      return;
    }

    goPath(getPathAfterLoop(pageList, formData, pathname, arrayPath));
  };

/**
 * Normalizes an arrayBuilder loop's `arrayBuilderOptions.required` into the
 * `(formData) => boolean` shape expected by `ArrayBuilderCards`.
 *
 * Step 4's optional multi-page response loops declare literal
 * `required: false` rather than a function; `arrayBuilderPages()` normalizes
 * that internally before `ArrayBuilderCards` is reached via a loop's own
 * summary/review page. The rolled-up supporting evidence summary page calls
 * `ArrayBuilderCards` directly across multiple loops, bypassing that
 * normalization — hence this helper.
 *
 * @param {((formData: object) => boolean) | boolean | undefined} required
 * @returns {(formData: object) => boolean}
 */
export const normalizeArrayBuilderRequired = required =>
  typeof required === 'function' ? required : () => required;
